import Vue from 'vue'
import Vuex from 'vuex'
import moment from 'moment'
import repositories from '@/repositories/RepositoryFactory'
import EVALUATION_OPTIONS from '@/const/evaluationoptions'
import CUSTOMER_SEGMENTS from '@/const/customersegments'
import EVALUATIONS from '@/const/evaluations'
import EVALUATIONS_POS from '@/const/evaluationsPos'
import { getLabels } from '@/util/labels'
import {
  SET_SERVICE_STATUS,
  SET_ACCOUNT,
  SET_SITE,
  SET_COMPANIES,
  SET_ITEM_CATEGORY_LEVELS,
  SET_MENUS,
  SET_CALENDAR,
  SET_RESERVE_MAX_DATE,
  SET_ANNOUNCEMENT,
  ENABLE_SEASON_FEATURE,
  SET_MENU_DATA_TYPE,
  SET_IS_QUERY_FULL_FEATURED,
  SET_QUERY_FILTER_RESTORING,
} from '@/const/mutation-types'
import { LOAD_BASIC_MASTERS } from '@/const/action-types'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    // サービス稼働状態
    service: {
      available: false,
      message: undefined,
    },
    // アカウント情報
    account: {
      loginId: undefined,
      roles: [],
      services: [],
    },
    site: {
      hasLoss: false,
      hasStock: false,
      hasPosTime: false,
    },
    // データ保持期間マスタ
    calendar: {
      minDate: undefined,
      maxDate: undefined,
      reserveMaxDate: undefined,
    },
    // 企業マスタ（ログインユーザーが参照可能な企業のみ）
    companies: [],
    // 分類階層マスタ（ログインユーザーが参照可能な企業毎の階層すべて）
    // Storeに入れるにはアレですが、、企業コードをキーに階層配列を保存します。
    // {
    //   cyberlinks: [ ...levels ],
    // }
    itemCategoryLevels: {},
    // 分析条件の復元処理完了フラグ
    queryFilterRestoring: false,
    // メニューマスタ（ログインユーザーが参照可能なメニューのみ）
    menus: [],
    idposMenus: [],
    posMenus: [],
    // フル機能利用可否
    // このフラグは各分析画面（分析結果の閲覧）でのみ有効です。メニューやその他画面では意味を持ちません。
    isQueryFullFeatured: false,
    // お知らせ
    announcement: {
      hasUnread: false,
    },
    // 個人設定
    preferences: {
      enableSeasonFeature: false,
    },
    // メニューで選択されているデータ種別
    menuDataType: undefined,
    // 企業毎変わるようなラベルです
    // .envの定義を参照し、なければデフォルト値を設定します。
    labels: {
      evaluationOption: getLabels(
        EVALUATION_OPTIONS,
        {
          ratio: '構成比',
          val: '実数',
        },
        'VUE_APP_LABEL_EVALUATION_OPTIONS',
      ),
      customerSegment: getLabels(
        CUSTOMER_SEGMENTS,
        {
          rank: 'ランク',
          pfv: 'PFV',
          age: '年代',
        },
        'VUE_APP_LABEL_CUSTOMER_SEGMENT',
      ),
      evaluations: getLabels(
        EVALUATIONS,
        {
          amount: '金額',
          qty: '点数',
          receipt_count: '客数',
          customer_count: 'ID数',
          amount_per_qty: '点単価',
          qty_per_receipt: '客点数',
          amount_per_receipt: '客単価',
          receipt_per_customer: 'ID回数',
          qty_per_customer: 'ID点数',
          amount_per_customer: 'ID金額',
          total_amount: '総金額',
          total_qty: '総点数',
          total_receipt_count: '総客数',
        },
        'VUE_APP_LABEL_EVALUATIONS',
      ),
      evaluationsPos: getLabels(
        EVALUATIONS_POS,
        {
          amount: '金額',
          qty: '点数',
          amount_per_qty: '点単価',
          qty_pi: '点数PI値',
          amount_pi: '金額PI値',
          receipt_count: '客数',
          sku_count: 'SKU数',
          store_count: '店舗数',
          avg_ymd: '平均日数',
          last_sale_ymd: '最終販売日',
          discount_amount: '値引金額',
          dispose_amount: '廃棄金額',
          loss_amount: 'ロス金額',
          stock_qty: '在庫数',
          stock_turn_over_rate: '在庫回転率',
          stock_turn_over_ymd: '在庫回転日数',
        },
        'VUE_APP_LABEL_EVALUATIONS_POS',
      ),
    },
  },
  getters: {
    /**
     * ログインユーザーが管理者権限を持つか
     */
    isAdmin: state => {
      if (!state.account.roles) {
        return false
      }
      return state.account.roles.includes('admin')
    },
    isHead: state => {
      if (!state.account.roles) {
        return false
      }
      return state.account.roles.includes('head')
    },
    isDeveloper: state => {
      if (!state.account.roles) {
        return false
      }
      return state.account.roles.includes('developer')
    },
    calendarEffectiveMaxDate: state => {
      return state.calendar.reserveMaxDate || state.calendar.maxDate
    },
    calendarEffectiveMinDate: state => {
      const { reserveMaxDate, maxDate, minDate } = state.calendar
      if (reserveMaxDate && maxDate && minDate) {
        const reserveMax = moment(String(reserveMaxDate), 'YYYYMMDD', true)
        const max = moment(String(maxDate), 'YYYYMMDD', true)
        const min = moment(String(minDate), 'YYYYMMDD', true)
        if (reserveMax.isValid() && max.isValid() && min.isValid()) {
          const offsetDays = reserveMax.diff(max, 'days')
          const effectiveMinDate = min
            .clone()
            .add(offsetDays, 'days')
            .format('YYYYMMDD')
          return effectiveMinDate
        }
      }
      return minDate
    },
  },
  mutations: {
    [SET_SERVICE_STATUS](state, { available, message }) {
      state.service.available = available
      state.service.message = message
    },
    [SET_ACCOUNT](state, { userId, companyCode, loginId, roles, services }) {
      state.account.userId = userId
      state.account.companyCode = companyCode
      state.account.loginId = loginId
      state.account.roles = roles
      state.account.services = services
    },
    [SET_SITE](state, { hasLoss, hasStock, hasPosTime }) {
      state.site.hasLoss = hasLoss
      state.site.hasStock = hasStock
      state.site.hasPosTime = hasPosTime
    },
    [SET_COMPANIES](state, companies) {
      state.companies = companies
    },
    [SET_ITEM_CATEGORY_LEVELS](state, itemCategoryLevels) {
      state.itemCategoryLevels = itemCategoryLevels
    },
    [SET_MENUS](state, menus) {
      state.menus = menus
      state.idposMenus = menus.filter(m => m.dataType === 'ID-POS')
      state.posMenus = menus.filter(m => m.dataType === 'POS')
    },
    [SET_CALENDAR](state, { from, to }) {
      state.calendar.minDate = from
      state.calendar.maxDate = to
    },
    [SET_RESERVE_MAX_DATE](state, value) {
      state.calendar.reserveMaxDate = value
    },
    [SET_ANNOUNCEMENT](state, { hasUnread }) {
      state.announcement.hasUnread = hasUnread
    },
    [ENABLE_SEASON_FEATURE](state, value) {
      state.preferences.enableSeasonFeature = value
    },
    [SET_MENU_DATA_TYPE](state, value) {
      state.menuDataType = value
    },
    [SET_IS_QUERY_FULL_FEATURED](state, value) {
      state.isQueryFullFeatured = value
    },
    [SET_QUERY_FILTER_RESTORING](state, value) {
      state.queryFilterRestoring = value
    },
  },
  actions: {
    /**
     * 共通で必要となる（Storeに保存する）マスタをロードします。
     */
    async [LOAD_BASIC_MASTERS]({ commit }, { companyCode, loginId }) {
      // アカウント情報
      const account = await repositories
        .get('account')
        .get(companyCode, loginId)
      commit(SET_ACCOUNT, account)

      // サイト情報
      const site = await repositories.get('site').get()
      commit(SET_SITE, site)

      // メニュー
      const menus = await repositories.get('menu').get()
      commit(SET_MENUS, menus)

      // 企業一覧
      const companies = await repositories.get('company').get()
      commit(SET_COMPANIES, companies)

      // 分類階層（階層マスタは企業毎）
      const itemCategoryLevels = {}
      for (const { code } of companies) {
        const levels = await repositories.get('itemCategoryLevel').get(code)
        itemCategoryLevels[code] = levels
      }
      commit(SET_ITEM_CATEGORY_LEVELS, itemCategoryLevels)

      // データ保持期間
      const calendar = await repositories.get('calendar').get()
      commit(SET_CALENDAR, calendar)
    },
  },
  // https://vuex.vuejs.org/ja/guide/strict.html
  strict: process.env.NODE_ENV === 'development',
})
